/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, forwardRef, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ViewChild, ElementRef, Inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CancelDialogService, ClickOutsideDirective, CreateCustomDialogService, ISearchDropDownBadge, PreventLeadingSpaceDirective } from '@sml/utility';
import { IconsComponent } from '@sml/icons';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {BadgeComponent, BadgeType} from '@sml/badge'


type Position = 'top' | 'bottom';

@Component({
  selector: 'sml-search-dropdown',
  templateUrl: './search-dropdown.component.html',
  styleUrls: ['./search-dropdown.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ClickOutsideDirective, IconsComponent, PreventLeadingSpaceDirective, BadgeComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchDropdownComponent),
      multi: true,
    },
  ],
})
export class SearchDropdownComponent implements OnInit, OnChanges, ControlValueAccessor {
  @ViewChild('searchDropdownContainer') searchDropdownContainer!: ElementRef;
  @ViewChild('searchDropdownMenu') searchDropdownMenu!: ElementRef;
  @ViewChild('searchDropdownInput') searchDropdownInput!: ElementRef;
  @Input() items: { label: string; value: number, iconName?:string, badge?: ISearchDropDownBadge}[] = [];
  @Input() placeholder = 'Search...';
  @Input() isInvalid = false;
  @Input() idSufix = '';
  @Input() disabled = false;
  @Input() customEmptyState=false;
  @Input() isVolunteerActivity = false;
  @Input() position: Position | '' = '';
  @Input() isRegistrationItem=false
  @Input() hideCrossButton=false;
  @Input() showCreateCustomForm=false;
  @Input() readonly=false;
  @Output() controlTouched = new EventEmitter<FormControl>();
  @Output() controlDirty = new EventEmitter<FormControl>();
  @Output() createCustomFormClick = new EventEmitter<void>();
  public isMobile=false;
  public lastValue: any = null;
  @Input() selectedInputValue:{ label: string; value: number , iconName?:string, badge?: ISearchDropDownBadge} | null = null;
  badgeTypes: BadgeType = 'grey';
  public searchTerm = '';
  public control: FormControl = new FormControl();
  public filteredItems: { label: string; value: number,iconName?:string, badge?: ISearchDropDownBadge }[] = [];
  public isDropdownOpen = false;
  public selectedValue: { label: string; value: number,iconName?:string, badge?: ISearchDropDownBadge } | null = null;

  private onChange: (value: any) => void = () => {
    // Empty
  };
  private onTouched: () => void = () => {
    // Empty
  };
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly cd: ChangeDetectorRef,
    private readonly createCustomDialogService: CreateCustomDialogService,

  ) { }

  ngOnInit() {
    this.filteredItems = [...this.items];
    this.checkWindowSize();
  }
   checkWindowSize(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 768;
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      const control = changes['control'].currentValue;
      if (control) {
        this.control = control;
      }
    }
    if(changes['items']){
      this.filteredItems = [...this.items];
      this.updatedSelectedItem(this.lastValue);
    }
    if(changes['selectedInputValue']){
      this.selectedValue = changes['selectedInputValue'].currentValue;
    }
    this.setDisabledState(this.disabled);
    this.cd.detectChanges();
  }

  filterItems() {
    const search = this.searchTerm.toLowerCase();
    this.filteredItems = this.items.filter((item) =>
      item.label.toLowerCase().includes(search)
    );
  }

  showAllItems(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    this.filteredItems = [...this.items];
  
    if (!isPlatformBrowser(this.platformId) || !this.searchDropdownContainer) return;
  
    const input = this.searchDropdownInput?.nativeElement as HTMLInputElement;
    if (!input) return;
  
    setTimeout(() => this.adjustDropdownPosition(input), 200);
  }
  
  private adjustDropdownPosition(input: HTMLInputElement): void {
    const dropdownMenuContainer = this.searchDropdownContainer.nativeElement as HTMLElement;
    const dropdownMenu = this.searchDropdownMenu?.nativeElement as HTMLElement;
    if (!dropdownMenu) return;
  
    dropdownMenu.classList.remove('tw-opacity-0');
    
    const { top, height } = input.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const dropdownHeight = dropdownMenu.offsetHeight;
    const spaceBelow = window.innerHeight - (top + height + scrollTop);
  
    if (this.position === 'bottom') {
      dropdownMenuContainer.classList.add('search-dropdown-bottom');
    } else if (this.position === 'top') {
      dropdownMenuContainer.classList.add('search-dropdown-top');
    } else {
      this.toggleDropdownPosition(dropdownMenuContainer, spaceBelow, dropdownHeight);
    }
  }
  
  private toggleDropdownPosition(container: HTMLElement, spaceBelow: number, dropdownHeight: number): void {
    const positionClass = spaceBelow < dropdownHeight ? 'search-dropdown-top' : 'search-dropdown-bottom';
    container.classList.toggle('search-dropdown-top', positionClass === 'search-dropdown-top');
    container.classList.toggle('search-dropdown-bottom', positionClass === 'search-dropdown-bottom');
  }
  

  selectItem(item: { label: string; value: number , iconName?:string, badge?: ISearchDropDownBadge }): void {
    this.selectedValue = item;
    this.searchTerm = item.label;
    this.onChange(item.value); // Emit selected value
    this.onTouched();
    this.closeDropdown();
  }

  isSelected(item: { label: string; value: number }): boolean {
    return this.selectedValue?.value === item.value;
  }

  // ControlValueAccessor methods for Reactive Forms
  writeValue(value: any): void {
    this.lastValue = value;
    const selectedItem = this.items.find((item) => item.value === value);
    this.searchTerm = selectedItem ? selectedItem.label : '';
  }
  updatedSelectedItem(value: any): void {
    if(!value){
      return;
    }
   const selectedItem = this.items.find((item) => item.value === value);
    this.searchTerm = selectedItem ? selectedItem.label : '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if(isDisabled){
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (target?.value !== undefined) {
      this.searchTerm = target.value;
      this.filterItems();
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  clearSearchTerm(): void {
    this.searchTerm = '';
    this.selectedValue = null;
    this.filteredItems = [...this.items];
    this.onChange(null);
    this.onTouched();
  }

  markAsTouchedAndDirty(): void {
    if (this.control) {
      this.control.markAsTouched();
      this.control.markAsDirty();
      this.control.updateValueAndValidity();
      this.controlTouched.emit(this.control);
      this.controlDirty.emit(this.control);
    }
    this.onTouched();
  }

  onBlur(): void {
    this.markAsTouchedAndDirty();
  }
  
  onCreateCustomFormClick(): void {
    // Just open the dialog - app.component will handle the confirmation
    this.createCustomDialogService.openDialog();
  }
}
